import { Site, SLD } from '@patsnap-utils/biz';
import { v4 as uuidv4 } from 'uuid';

import WorkspaceSvg from '@/assets/icon/playbooks/workspace.svg';
import MindSvg from '@/assets/icon/playbooks/mind.svg';
import FmeaSvg from '@/assets/icon/playbooks/fmea.svg';
import ResearchSvg from '@/assets/icon/playbooks/research.svg';
import StructureSearchSvg from '@/assets/icon/playbooks/structure_search.svg';
import SubstanceSearchSvg from '@/assets/icon/playbooks/substance_search.svg';
import PropertySearchSvg from '@/assets/icon/playbooks/property_search.svg';
import MetalSearchSvg from '@/assets/icon/playbooks/metal_search.svg';
import MaterialGeneralSvg from '@/assets/icon/playbooks/material_general.svg';
import WorkflowSearchSvg from '@/assets/icon/playbooks/workflow_search.svg';
import MaterialMindSvg from '@/assets/icon/playbooks/material_mind.svg';
import CompanySearchSvg from '@/assets/icon/playbooks/company_search.svg';
import CompetitiveLandscapeSvg from '@/assets/icon/playbooks/competitor_landscape.svg';
import MarketReportSvg from '@/assets/icon/playbooks/market_report.svg';
// ip
import ProtectSmartSearchSvg from '@/assets/icon/playbooks/protect_smart_search.svg';
import NoveltyCheckSvg from '@/assets/icon/playbooks/novelty_check.svg';
import AIImageSearchSvg from '@/assets/icon/playbooks/ai_image_search.svg';
import AIFTOSearchSvg from '@/assets/icon/playbooks/ai_fto_search.svg';
import AIPatentDisclosureSvg from '@/assets/icon/playbooks/ai_patent_disclosure.svg';
import AITranslationSvg from '@/assets/icon/playbooks/ai_translation.svg';
import AISpecificationSvg from '@/assets/icon/playbooks/ai_specification.svg';
import AINoveltySearchSvg from '@/assets/icon/playbooks/ai_novelty_search.svg';
import IpProfileExternalSvg from '@patsnap-ui/icon/assets/solid/ArrowTailRight.svg';
import RoadmapSvg from '@/assets/icon/playbooks/roadmap.svg';
// ls
import LSChatSvg from '@/assets/icon/playbooks/ls_chat.svg';
import LSDocumentQASvg from '@/assets/icon/playbooks/ls_document_qa.svg';
import LSMarkushDraftSvg from '@/assets/icon/playbooks/ls_markush_draft.svg';
import LSIntelligenceMonitorSvg from '@/assets/icon/playbooks/ls_intelligence_monitor.svg';

import CopilotBaseIcon from '@/assets/icon/copilot-base.svg';

const playbookPathConfig = {
  workspace: {
    icon: WorkspaceSvg,
    path: () => new Site({ sld: SLD.WORKSPACE }).href,
  },
  protect_smart_search: {
    icon: ProtectSmartSearchSvg,
    path: () => '/general-search',
  },
  novelty_check: {
    icon: NoveltyCheckSvg,
    path: () => '/idea',
  },
  ai_image_search: {
    icon: AIImageSearchSvg,
    path: () => '/ai-ip/image-fto-search',
  },
  ai_fto_search: {
    icon: AIFTOSearchSvg,
    path: () => `/ai-drafting/fto-pro/${Date.now()}`,
  },
  ai_patent_disclosure: {
    icon: AIPatentDisclosureSvg,
    path: () => `/ai-drafting/disclosure/${Date.now()}`,
  },
  ai_translation: {
    icon: AITranslationSvg,
    path: () => `/ai-drafting/translation/${Date.now()}`,
  },
  ai_specification: {
    icon: AISpecificationSvg,
    path: () => `/ai-drafting/specification/${Date.now()}`,
  },
  ai_novelty_search: {
    icon: AINoveltySearchSvg,
    path: () => `/ai-drafting/novelty-check-report/${Date.now()}`,
  },
  ip_profile_external: {
    icon: IpProfileExternalSvg,
    path: () => '',
  },
  roadmap: {
    icon: RoadmapSvg,
    path: () => '/roadmap',
  },
  mind: {
    icon: MindSvg,
    path: () => '/whiteboard',
  },
  fmea: {
    icon: FmeaSvg,
    path: () => '/fmea',
  },
  research: {
    icon: ResearchSvg,
    path: () => '/project',
  },
  mse_playbook_full_dimensional_search: {
    icon: MaterialGeneralSvg,
    internal: true,
    path: () => '/general-search',
  },
  structure_search: {
    icon: StructureSearchSvg,
    internal: true,
    path: () => '/structure',
  },
  substance_search: {
    icon: SubstanceSearchSvg,
    internal: true,
    path: () => '/substance',
  },
  property_search: {
    icon: PropertySearchSvg,
    internal: true,
    path: () => '/property',
  },
  metal_search: {
    icon: MetalSearchSvg,
    internal: true,
    path: () => '/composition',
  },
  workflow_search: {
    icon: WorkflowSearchSvg,
    internal: true,
    path: () => '/workflow',
  },
  mse_doc_analyzer: {
    icon: LSDocumentQASvg,
    path: () => '/material/analyzer',
  },
  material_mind: {
    icon: MaterialMindSvg,
    path: () => '/whiteboard/#/material/',
  },
  insights_company_search: {
    icon: CompanySearchSvg,
    path: () => '/company-search',
  },
  insights_competitive_landscape: {
    icon: CompetitiveLandscapeSvg,
    path: () => '/competitive-landscape',
  },
  insights_markets_analysis: {
    icon: MarketReportSvg,
    path: () => '/discovery/#/market-category',
  },
  ls_chat: {
    icon: LSChatSvg,
    path: () => '/ai-ls/chat-agent',
  },
  ls_document_qa: {
    icon: LSDocumentQASvg,
    path: () => '/ai-ls/document-agent',
  },
  ls_mark_patent: {
    icon: LSMarkushDraftSvg,
    path: () => '/ai-ls/markush-draft-agent',
  },
  ls_intelligence_monitor: {
    icon: LSIntelligenceMonitorSvg,
    path: () => `/ai-ls/intelligence-monitor-agent/${uuidv4()}`,
  },
};

export function getPlaybookPath(playbook: string) {
  return playbookPathConfig[playbook]?.path() || '';
}

export function getPlaybookIcon(playbook: string) {
  return playbookPathConfig[playbook]?.icon || CopilotBaseIcon;
}

export function ifPlaybookInternal(playbook: string) {
  return playbookPathConfig[playbook as keyof typeof playbookPathConfig]?.internal ?? false;
}
